// Owner console → Access codes. A code is the whole deal the Sales side agreed:
// an Organisation or an Individual practitioner, staff seats, family licences
// and the price per licence per month. The new code is shown ONCE, with copy.

import { useState } from "react";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary } from "../../i18n/dictionary";
import { getPortalData } from "../../data";
import type { OrgKind } from "../../data/types";
import {
  Button,
  Card,
  ErrorPanel,
  Field,
  INPUT,
  LoadingLine,
  Notice,
  TableCard,
  TD,
  TH,
  endOfDay,
  formatDate,
  formatMoney,
  todayIso,
  useLoad,
} from "../admin/ui";
import { consoleErrorCopy, orgKeeps, shareProblem } from "./ConsoleOrgs";

/** Listed codes are masked: the full code is shown once, when it is created. */
export function maskCode(code: string): string {
  return code.length <= 8 ? code : `${code.slice(0, 8)}••••`;
}

export function ConsoleCodes() {
  const { locale } = useComfort();
  const t = getDictionary(locale).console;
  const load = useLoad(() => getPortalData().consoleAccessCodes(), []);

  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<OrgKind>("organisation");
  const [seats, setSeats] = useState("5");
  const [families, setFamilies] = useState("10");
  // The Malaysian default (2026-10-23): the family pays the organisation RM40
  // a month, Zooylo's share is RM30, and 8% SST is added to Zooylo's invoice.
  const [retail, setRetail] = useState("40");
  const [price, setPrice] = useState("30");
  const [sst, setSst] = useState("8");
  const [currency, setCurrency] = useState("MYR");
  const [uses, setUses] = useState("1");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function chooseKind(k: OrgKind) {
    setKind(k);
    // An individual practitioner is one seat by definition.
    if (k === "individual") setSeats("1");
    else if (seats === "1") setSeats("5");
  }

  async function create() {
    const nums = [seats, families, price, uses].map((v) => Number(v));
    if (nums.some((n) => !Number.isFinite(n) || n < 0) || Number(uses) < 1 || !/^[A-Za-z]{3}$/.test(currency.trim())) {
      setError(t.errInvalid);
      return;
    }
    const problem = shareProblem(t, retail, price, sst);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");
    setCreated(null);
    setCopied(false);
    try {
      const code = await getPortalData().consoleCreateAccessCode({
        label: label.trim(),
        kind,
        seats: Math.max(1, Math.round(Number(seats))),
        familyAllowance: Math.round(Number(families)),
        familyPrice: Number(price),
        familyRetailPrice: retail.trim() === "" ? 0 : Number(retail),
        sstRate: sst.trim() === "" ? 0 : Number(sst),
        currency: currency.trim().toUpperCase(),
        maxUses: Math.max(1, Math.round(Number(uses))),
        expiresAt: expires ? endOfDay(expires) : null,
      });
      setCreated(code);
      setLabel("");
      await load.refresh();
    } catch (err) {
      setError(consoleErrorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Card title={t.newCode} id="new-code">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <Field label={t.codeLabel} value={label} onChange={(e) => setLabel(e.target.value)} style={{ gridColumn: "1 / -1" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label htmlFor="code-kind" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
              {t.codeKind}
            </label>
            <select id="code-kind" value={kind} onChange={(e) => chooseKind(e.target.value as OrgKind)} style={INPUT}>
              <option value="organisation">{t.kindOrg}</option>
              <option value="individual">{t.kindIndividual}</option>
            </select>
          </div>
          <Field label={t.codeSeats} type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} />
          <Field label={t.codeAllowance} type="number" min={0} value={families} onChange={(e) => setFamilies(e.target.value)} />
          <Field label={t.codeRetailPrice} type="number" min={0} step="0.01" inputMode="decimal" value={retail} onChange={(e) => setRetail(e.target.value)} />
          <Field label={t.codePrice} type="number" min={0} step="0.01" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
          <Field
            label={t.orgKeeps}
            readOnly
            tabIndex={-1}
            value={formatMoney(locale, orgKeeps(retail, price), currency.trim().toUpperCase() || "MYR")}
            hint={t.orgKeepsHint}
          />
          <Field label={t.sstRate} type="number" min={0} max={100} step="0.01" inputMode="decimal" value={sst} onChange={(e) => setSst(e.target.value)} />
          <Field label={t.currency} maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value)} />
          <Field label={t.codeUses} type="number" min={1} value={uses} onChange={(e) => setUses(e.target.value)} />
          <Field label={t.codeExpires} type="date" min={todayIso()} value={expires} onChange={(e) => setExpires(e.target.value)} />
        </div>
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => void create()} disabled={busy}>
            {busy ? t.creating : t.create}
          </Button>
        </div>
        <div role="status" aria-live="polite" style={{ marginTop: error || created ? 12 : 0 }}>
          {error && <p style={{ margin: 0, color: "var(--peach-deep)", fontWeight: 600 }}>{error}</p>}
          {created && (
            <Notice tone="good" title={t.newCodeTitle}>
              <span style={{ display: "block" }}>{t.newCodeBody}</span>
              <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
                <code data-testid="new-code" style={{ fontSize: 20, fontWeight: 700, letterSpacing: ".06em", color: "var(--ink-deep)", overflowWrap: "anywhere" }}>
                  {created}
                </code>
                <Button variant="secondary" onClick={() => void copy()}>
                  {copied ? t.copied : t.copy}
                </Button>
              </span>
            </Notice>
          )}
        </div>
      </Card>

      <section aria-labelledby="codes-title">
        <h2 id="codes-title" style={{ fontSize: 18, color: "var(--ink-deep)", margin: "0 0 10px" }}>
          {t.codesTitle}
        </h2>
        {load.loading && !load.data ? (
          <LoadingLine label={t.loading} />
        ) : load.error || !load.data ? (
          <ErrorPanel title={t.errorTitle} message={consoleErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.retry} />
        ) : load.data.length === 0 ? (
          <Card>
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.noCodes}</p>
          </Card>
        ) : (
          <TableCard label={t.codesTitle} minWidth={760}>
            <thead>
              <tr>
                <th scope="col" style={TH}>{t.colCode}</th>
                <th scope="col" style={TH}>{t.colLabel}</th>
                <th scope="col" style={TH}>{t.colKind}</th>
                <th scope="col" style={TH}>{t.colTerms}</th>
                <th scope="col" style={TH}>{t.colUses}</th>
                <th scope="col" style={TH}>{t.colExpires}</th>
              </tr>
            </thead>
            <tbody>
              {load.data.map((c) => (
                <tr key={c.code}>
                  <td style={{ ...TD, fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }}>{maskCode(c.code)}</td>
                  <td style={TD}>{c.label || "—"}</td>
                  <td style={TD}>{c.kind === "individual" ? t.kindIndividual : t.kindOrg}</td>
                  <td style={{ ...TD, fontSize: 13.5 }}>
                    {fill(t.termsLine, {
                      seats: c.seatsTotal,
                      families: c.familyAllowance,
                      price: formatMoney(locale, c.familyPriceMonthly, c.currency),
                    })}
                  </td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>
                    {c.uses} / {c.maxUses}
                  </td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>{c.expiresAt ? formatDate(locale, c.expiresAt) : t.never}</td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        )}
      </section>
    </div>
  );
}
